import React, { useEffect } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useAnalytics } from '../context/AnalyticsContext';
import './Landing.css';

const About: React.FC = () => {
  const { trackPageView } = useAnalytics();

  useEffect(()=>{
    window.scrollTo(0, 0);
  },[])

  useEffect(() => {
    trackPageView('/about', 'About');
  }, [trackPageView]);

  return (
    <div className="app">
      <Header />
      
      {/* Main Content */}
      <main className="main-content">
        {/* Hero Section */}
        <section className="hero">
          <div className="hero-content">
            <h1 className="hero-title">About Aika</h1>
            <p className="hero-subtitle">
              Making summer camp scheduling easier for busy families through intelligent recommendations and seamless coordination.
            </p>
          </div>
        </section>

        {/* What We Do Section */}
        <section className="about-content">
          <div className="content-container">
            <div className="about-section">
              <h2 className="section-title">What We Do</h2>
              <div className="section-content">
                <div className="section-text">
                  <p>
                    Aika is a revolutionary camp scheduling platform designed specifically for busy families who want to make the task of scheduling kids' camps easier, more efficient, and less stressful.
                  </p>
                  <p>
                    We understand that coordinating summer camps, activities, and schedules can be overwhelming for parents. That's why we've built an intelligent platform that saves time and effort while making coordination between families seamless.
                  </p>
                  <p>
                    Our AI-powered system analyzes your family's needs, preferences, and schedule to recommend the perfect camp experiences for your children, eliminating the hours of research and planning that typically goes into summer camp coordination.
                  </p>
                  <p>
                    Aika means "time" in Finnish, and our goal is to save you time so you can spend more time with your family.
                  </p>
                </div>
                <div className="section-image">
                  <img src="/images/science.png" alt="Families planning summer activities together" />
                </div>
              </div>
            </div>

            {/* How We Help Section */}
            <div className="about-section">
              <h2 className="section-title">How We Help Families</h2>
              <div className="section-content">
                <div className="section-image">
                  <img src="/images/football.png" alt="Kids enjoying summer camp activities" />
                </div>
                <div className="section-text">
                  <p>
                    <strong>Save Time:</strong> Our intelligent recommendations eliminate hours of research and comparison shopping. Find the perfect camps in minutes, not days.
                  </p>
                  <p>
                    <strong>Reduce Stress:</strong> No more juggling multiple spreadsheets or trying to communicate schedules to your partner or your children's caretakers. Our platform provides a one-stop source of truth for all of your camp information.
                  </p>
                  <p>
                    <strong>Better Coordination:</strong> Easily coordinate with other families, share schedules, and plan group activities together. Make summer planning a collaborative, enjoyable experience.
                  </p>
                  <p>
                    <strong>Personalized Experience:</strong> Our AI learns your family's preferences and gets better at recommending camps that match your children's interests and your family's schedule.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Mission Section */}
        <section className="mission-section">
          <div className="mission-container">
            <h2 className="mission-title">Our Mission</h2>
            <p className="mission-statement">
              "Connecting families with enriching camp experiences through intelligent recommendations"
            </p>
          </div>
        </section>

        {/* Values Section */}
        <section className="values-section">
          <div className="content-container">
            <h2 className="section-title">Our Values</h2>
            <div className="values-grid">
              <div className="value-card">
                <div className="value-icon">⏰</div>
                <h3 className="value-title">Making life easier for families</h3>
                <p className="value-description">
                  We simplify the busywork of camp planning so parents can spend less time scheduling and more time enjoying family life.
                </p>
              </div>
              <div className="value-card">
                <div className="value-icon">🎯</div>
                <h3 className="value-title">Technology that serves people</h3>
                <p className="value-description">
                  We design tools thoughtfully and responsibly, always with the goal of supporting families and communities.
                </p>
              </div>
              <div className="value-card">
                <div className="value-icon">🌟</div>
                <h3 className="value-title">Quality Experiences</h3>
                <p className="value-description">
                  We connect kids with camps that nurture growth, joy, and lifelong memories.
                </p>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default About;
